import React from "react";
import { useState, useContext } from "react";
import { useNavigate, useParams } from 'react-router-dom';

import Loader from './common/Loader';
import ConfirmationModal from './common/ConfirmationModal';

import axios_api from "../Api";
import { MEDIA_URL, API_URL, AUTH_TOKEN } from '../Context';


export default function AssetCreateEdit(){

  const media_url = useContext(MEDIA_URL);
  const api_url = useContext(API_URL);
  const token = useContext(AUTH_TOKEN);

  const navigate = useNavigate();
  const { assetId } = useParams();

  const isEditMode = !!assetId;

  const [loading, setLoading] = useState(isEditMode);
  const [formError, setFormError] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  // Вычисляем доступные годы
  const currentYear = new Date().getFullYear();
  const availableYears = [currentYear - 1, currentYear, currentYear + 1];
  
  const [assetData, setAssetData] = useState({
    name: '',
    type: 'FILE',
    link: '',
    file: null,
    year: currentYear, // Текущий год по умолчанию
    category: ''
  });


  // Загрузка категорий
  React.useEffect(() => {
    axios_api.get(api_url + '/asset_categories/', {
      headers: {"Authorization": token},
    })
    .then(
      (result) => {
        setCategories(result.data);
        setCategoriesLoading(false);
      },
      (error) => {
        console.log('Ошибка загрузки категорий:', error);
        setCategories([]);
        setCategoriesLoading(false);
      }
    );
  }, [api_url, token]);


  // Загрузка данных asset при редактировании
  React.useEffect(() => {
    if (isEditMode) {
      axios_api.get(api_url + `/manage_asset/${assetId}`, {
        headers: {"Authorization": token},
      })
      .then(
        (result) => {
          const asset = result.data;
          setAssetData({
            name: asset.name || '',
            type: asset.type || '',
            link: asset.link || '',
            file: null,
            fileURL: asset.file,
            year: asset.year || currentYear,
            category: asset.category || ''
          });
          setLoading(false);
        },
        (error) => {
          console.log(error);
          setFormError('Материал не найден');
          setLoading(false);
        }
      );
    }
  }, [assetId, isEditMode, api_url, token]);

  const handleChange = (event) => {
    const name = event.target.id;
    let value = event.target.value;
    
    const assetDataNext = structuredClone(assetData);
    
    if (name === 'type') {
      // При смене типа очищаем соответствующие поля
      if (value === 'LINK') {
        assetDataNext.file = null;
      } else if (value === 'FILE') {
        assetDataNext.link = '';
      }
    }
    
    assetDataNext[name] = value;
    setAssetData(assetDataNext);
    setFormError(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const assetDataNext = structuredClone(assetData);
    assetDataNext.file = file;
    assetDataNext.name = assetDataNext.name === '' ? file.name : assetDataNext.name
    setAssetData(assetDataNext);
    setFormError(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormError(false);

    // Валидация
    if (!assetData.name) {
      setFormError('Необходимо указать название материала');
      return;
    }

    if (!assetData.type) {
      setFormError('Необходимо выбрать тип материала');
      return;
    }

    if (assetData.type === 'LINK' && !assetData.link) {
      setFormError('Для материала типа LINK необходимо указать ссылку');
      return;
    }

    if (assetData.type === 'LINK' && assetData.link) {
      const validProtocols = ['http://', 'https://', 'ftp://'];
      const hasValidProtocol = validProtocols.some(protocol => 
        assetData.link.toLowerCase().startsWith(protocol)
      );
      
      if (!hasValidProtocol) {
        setFormError('Ссылка должна начинаться с http://, https:// или ftp://');
        return;
      }
    }

    if (assetData.type === 'FILE' && !assetData.file && !isEditMode) {
      setFormError('Для материала типа FILE необходимо загрузить файл');
      return;
    }

    // Подготовка данных для отправки
    const formData = new FormData();
    formData.append('name', assetData.name);
    formData.append('type', assetData.type);
    formData.append('year', assetData.year);
    
    if (assetData.category) {
      formData.append('category', assetData.category);
    }
    
    if (assetData.type === 'LINK') {
      formData.append('link', assetData.link);
    } else if (assetData.type === 'FILE' && assetData.file) {
      formData.append('file', assetData.file);
    }

    if (isEditMode) {
      // Обновление существующего asset
      axios_api.put(api_url + `/manage_asset/${assetId}`, formData, {
        headers: {
          "Authorization": token,
          "Content-Type": "multipart/form-data",
        }
      })
      .then(
        (result) => {
          console.log("Asset updated");
          navigate("/asset_list");
          navigate(0);
        },
        (error) => {
          const errorMessage = error.response?.data || 'Ошибка обновления материала';
          setFormError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
          console.log(error);
        }
      );
    } else {
      // Создание нового asset
      axios_api.post(api_url + `/manage_asset/`, formData, {
        headers: {
          "Authorization": token,
          "Content-Type": "multipart/form-data",
        }
      })
      .then(
        (result) => {
          console.log("Asset created");
          navigate("/asset_list");
          navigate(0);
        },
        (error) => {
          const errorMessage = error.response?.data || 'Ошибка добавления материала';
          setFormError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
          console.log(error);
        }
      );
    }
  };

  const handleDelete = () => {
    axios_api.delete(api_url + `/manage_asset/${assetId}`, {
      headers: {"Authorization": token},
    })
    .then(
      (result) => {
        console.log("Asset deleted");
        navigate("/asset_list");
        navigate(0);
      },
      (error) => {
        const errorMessage = error.response?.data || 'Ошибка удаления материала';
        setFormError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
        console.log(error);
      }
    );
  };

  if (loading) {
    return <Loader/>;
  }

  return (
    <>
      <div className="row">
        <h3 className="m-3">{isEditMode ? 'Редактировать материал' : 'Добавить материал'}</h3>
      </div>
      <div className='row'>
        <div className="col-6 ms-3 lk-form">
          <form onSubmit={handleSubmit}>

            <div className="input-group my-3">
              <label className="mt-3">
                <p>название</p>
                <input 
                  id="name" 
                  type="text" 
                  className="form-control" 
                  value={assetData.name} 
                  onChange={handleChange}
                  //required
                />
              </label>
            </div>

            <div className="input-group my-3">
              <label className="mt-3">
                <p>год</p>
                <select 
                  id="year" 
                  className="lk-select form-select" 
                  value={assetData.year} 
                  onChange={handleChange}
                  disabled={isEditMode}
                  style={{width: '200px'}}
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="input-group my-3">
              <label className="mt-3">
                <p>категория</p>
                {categoriesLoading ? (
                  <select className="lk-select form-select" disabled style={{width: '200px'}}>
                    <option>Загрузка...</option>
                  </select>
                ) : (
                  <select 
                    id="category" 
                    className="lk-select form-select" 
                    value={assetData.category} 
                    onChange={handleChange}
                    style={{width: '200px'}}
                  >
                    <option value="">- выбрать -</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                )}
              </label>
            </div>

            <div className="input-group my-3">
              <label className="mt-3">
                <p>тип</p>
                {assetData.type ?
                  <select 
                    id="type" 
                    className="lk-select form-select" 
                    value={assetData.type} 
                    onChange={handleChange}
                    style={{width: '200px'}}
                  >
                    <option value="FILE">файл</option>                    
                    <option value="LINK">ссылка</option>
                  </select> 
                :
                  <select 
                    id="type" 
                    className="lk-select form-select" 
                    value="" 
                    onChange={handleChange}
                    style={{width: '200px'}}
                  >
                    <option disabled value="">- выбрать -</option>
                    <option value="FILE">файл</option>
                    <option value="LINK">ссылка</option>

                  </select> 
                }
              </label>
            </div>

            {assetData.type === 'LINK' && (
              <div className="input-group my-3">
                <label className="mt-3">
                  <p>ссылка</p>
                  <input 
                    id="link" 
                    type="text" 
                    className="form-control" 
                    value={assetData.link} 
                    onChange={handleChange}
                    //required={assetData.type === 'LINK'}
                  />
                </label>
              </div>
            )}

            {assetData.type === 'FILE' && (
              <div className="input-group my-3">
                <label className="mt-3">
                  <p>файл</p>
                  {isEditMode && assetData.fileURL && (
                    <a className="" href={media_url + assetData.fileURL} target="_blank" rel="noopener noreferrer">текущий файл</a>
                  )}
                  <input 
                    id="file" 
                    type="file" 
                    className="form-control my-3" 
                    onChange={handleFileChange}
                    //required={assetData.type === 'FILE' && !isEditMode}
                  />
                </label>
              </div>
            )}

            {formError && (
              <p className="text-danger">{formError}</p>
            )}
            
            <div className="d-flex gap-2">
              <button type="submit" className="btn lk-button my-3">
                {isEditMode ? 'Сохранить' : 'Создать'}
              </button>
              
              {isEditMode && (
                <button 
                  type="button" 
                  className="btn btn-danger my-3 ms-3" 
                  data-bs-toggle="modal" 
                  data-bs-target="#deleteAssetModal"
                >
                  Удалить
                </button>
              )}
            </div>
          </form>
        </div>               
      </div>

      {isEditMode && (
        <ConfirmationModal
          modalId="deleteAssetModal"
          headerText="Удаление материала"
          messageText={['Вы уверены, что хотите удалить этот материал?']}
          confirmButtonText="Удалить"
          Action={handleDelete}
          danger={true}
        />
      )}
    </>
  )
}
